import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, Award, Gift, RotateCcw, SlidersHorizontal } from "lucide-react";
import {
  LineChart,
  Line,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from "recharts";
import { api } from "../../lib/api";
import { tierPillClass, TIER_COLOR, tierLabel } from "../../lib/adminTier";

const fmtNumber = (n) => Number(n || 0).toLocaleString();
const fmtMoney = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtMonthDay = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  return `${d.getMonth() + 1}/${d.getDate()}`;
};

const KpiCard = ({ label, value, sublabel, testId, big = false }) => (
  <div
    data-testid={testId}
    className="bg-napoli-marble border border-napoli-gold/20 p-5 flex flex-col gap-1"
  >
    <p className="font-body font-semibold text-[10px] tracking-[0.3em] uppercase text-napoli-espresso-soft">
      {label}
    </p>
    <p className={`font-display font-bold ${big ? "text-[28px] sm:text-[32px]" : "text-[26px] sm:text-[30px]"} text-napoli-oxblood leading-none`}>
      {value}
    </p>
    <p className="font-body italic text-[11px] text-napoli-espresso-soft">
      {sublabel}
    </p>
  </div>
);

const ChartFrame = ({ title, sublabel, testId, children, height = 240 }) => (
  <div
    data-testid={testId}
    className="bg-napoli-marble border border-napoli-gold/20 p-6"
  >
    <p className="font-body font-semibold text-[11px] tracking-[0.3em] uppercase text-napoli-oxblood">
      {title}
    </p>
    <p className="mt-1 font-body italic text-[12px] text-napoli-espresso-soft">
      {sublabel}
    </p>
    <div className="mt-5" style={{ width: "100%", height }}>
      {children}
    </div>
  </div>
);

// Recharts tooltip — Napoli themed
const Tip = ({ active, payload, label, valueFormatter }) => {
  if (!active || !payload || !payload.length) return null;
  return (
    <div className="bg-napoli-cream-warm border border-napoli-oxblood/30 px-3 py-2 shadow-md">
      <p className="font-body text-[11px] tracking-[0.18em] uppercase text-napoli-oxblood">
        {label}
      </p>
      <p className="font-display font-bold text-[18px] text-napoli-espresso leading-none mt-1">
        {valueFormatter ? valueFormatter(payload[0].value) : payload[0].value}
      </p>
    </div>
  );
};

const DashboardSkeleton = () => (
  <div className="animate-pulse space-y-6">
    <div className="h-12 w-64 bg-napoli-marble" />
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
      {Array.from({ length: 6 }).map((_, i) => (
        <div key={i} className="h-28 bg-napoli-marble" />
      ))}
    </div>
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <div className="h-72 bg-napoli-marble" />
      <div className="h-72 bg-napoli-marble" />
    </div>
  </div>
);

const RecentRow = ({ tx }) => {
  const isEarn = tx.type === "earn";
  const isRedeem = tx.type === "redeem";
  const isAdjust = tx.type === "admin_adjustment";
  const isVoid = tx.type === "void";
  const customerName = [tx.customer_first_name, tx.customer_last_name].filter(Boolean).join(" ").trim() || "—";
  const staffName = tx.staff_first_name ? [tx.staff_first_name, tx.staff_last_name].filter(Boolean).join(" ").trim() : null;
  const points = `${tx.points_delta > 0 ? "+" : ""}${tx.points_delta.toLocaleString()} pts`;
  const amount = tx.amount_spent != null ? `$${tx.amount_spent.toFixed(2)} check` : null;

  let icon = <Award size={15} />;
  let iconClass = "bg-napoli-gold/15 text-napoli-gold";
  let verbClass = "text-napoli-gold";
  let pointsClass = "text-napoli-gold";
  let verb = "awarded";
  if (isRedeem) {
    icon = <Gift size={15} />;
    iconClass = "bg-napoli-oxblood/20 text-napoli-rose";
    verbClass = "text-napoli-oxblood";
    pointsClass = "text-napoli-oxblood";
    verb = "redeemed";
  } else if (isAdjust) {
    icon = <SlidersHorizontal size={15} />;
    iconClass = "bg-napoli-rose/30 text-napoli-oxblood";
    verbClass = "text-napoli-oxblood";
    pointsClass = tx.points_delta >= 0 ? "text-napoli-gold" : "text-napoli-oxblood";
    verb = "adjusted";
  } else if (isVoid) {
    icon = <RotateCcw size={15} />;
    iconClass = "bg-napoli-espresso-soft/20 text-napoli-espresso-soft";
    verbClass = "text-napoli-espresso-soft";
    pointsClass = "text-napoli-espresso-soft";
    verb = "voided";
  }

  return (
    <li
      data-testid={`admin-recent-${tx.id}`}
      className="flex items-start gap-4 px-5 py-4 border-b border-napoli-gold/15 last:border-0 hover:bg-napoli-cream-warm/60 transition-colors"
    >
      <div aria-hidden className={`flex-shrink-0 mt-0.5 w-8 h-8 rounded-full flex items-center justify-center ${iconClass}`}>
        {icon}
      </div>
      <div className="flex-1 min-w-0">
        <p className="font-body text-[14px] text-napoli-espresso leading-snug">
          {staffName ? (
            <>
              <span className="font-semibold">{staffName}</span>{" "}
            </>
          ) : null}
          <span className={`font-medium ${verbClass}`}>{verb}</span>{" "}
          {isRedeem ? (
            <>
              <span className="font-medium italic text-napoli-espresso/85">
                {tx.reward_name || "a reward"}
              </span>{" "}
              <span className="font-medium text-napoli-espresso-soft">for</span>{" "}
              <span className="font-semibold">{customerName}</span>{" "}
              <span className={`font-bold ${pointsClass}`}>{points}</span>
            </>
          ) : (
            <>
              <span className="font-semibold">{customerName}</span>{" "}
              <span className={`font-bold ${pointsClass}`}>{points}</span>
              {amount ? (
                <span className="font-medium text-[13px] text-napoli-espresso-soft"> · {amount}</span>
              ) : null}
            </>
          )}
        </p>
        {tx.notes ? (
          <p className="mt-1 font-body italic text-[12px] text-napoli-espresso-soft">"{tx.notes}"</p>
        ) : null}
        <p className="mt-1.5 font-body font-medium text-[11px] tracking-[0.1em] uppercase text-napoli-espresso-soft/80">
          {tx.created_at_relative}
        </p>
      </div>
    </li>
  );
};

const AdminDashboardPage = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get("/admin/dashboard");
        if (alive) setData(data);
      } catch (e) {
        if (alive) setErr("Couldn't load dashboard data.");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  if (loading) {
    return (
      <section data-testid="admin-dashboard-loading" className="max-w-7xl mx-auto px-6 py-8">
        <DashboardSkeleton />
      </section>
    );
  }
  if (err || !data) {
    return (
      <section className="max-w-7xl mx-auto px-6 py-12 text-center">
        <p data-testid="admin-dashboard-error" className="font-body italic text-napoli-oxblood">
          {err || "Unable to load."}
        </p>
      </section>
    );
  }

  const { kpis, charts, recent_activity, greeting } = data;
  const tierData = (charts.tier_distribution || []).map((d) => ({
    name: tierLabel(d.tier),
    value: d.count,
    fill: TIER_COLOR[d.tier] || "#EFE3CE",
  }));
  const totalTierCount = tierData.reduce((s, d) => s + d.value, 0);

  return (
    <section data-testid="admin-dashboard" className="max-w-7xl mx-auto px-6 py-8">
      {/* Greeting band */}
      <div className="mb-8" data-testid="admin-greeting">
        <p className="font-body font-semibold text-[10px] tracking-[0.3em] uppercase text-napoli-oxblood">
          {greeting}
        </p>
        <h1 className="mt-2 font-display font-bold text-[32px] sm:text-[36px] text-napoli-espresso leading-tight">
          Welcome back, Owner.
        </h1>
        <p className="mt-2 font-body italic text-[14px] text-napoli-espresso-soft">
          Here's how Napoli is doing.
        </p>
      </div>

      {/* KPI grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        <KpiCard testId="kpi-total-customers" label="Total customers" value={fmtNumber(kpis.total_customers)} sublabel="All time" />
        <KpiCard testId="kpi-active-30d" label="Active 30d" value={fmtNumber(kpis.total_active_customers_30d)} sublabel="Last 30 days" />
        <KpiCard testId="kpi-points-30d" label="Points awarded" value={fmtNumber(kpis.total_points_awarded_30d)} sublabel="Last 30 days" />
        <KpiCard testId="kpi-redemptions-30d" label="Redemptions" value={fmtNumber(kpis.total_redemptions_30d)} sublabel="Last 30 days" />
        <KpiCard testId="kpi-revenue-30d" label="Check revenue" value={fmtMoney(kpis.total_check_revenue_30d)} sublabel="Last 30 days" />
        <KpiCard testId="kpi-avg-check" label="Avg check" value={fmtMoney(kpis.avg_check_size)} sublabel="Last 30 days" />
      </div>

      {/* Two-up charts */}
      <div className="mt-8 grid grid-cols-1 lg:grid-cols-2 gap-6">
        <ChartFrame
          testId="chart-signups"
          title="New customers"
          sublabel="Daily signups, last 30 days"
        >
          <ResponsiveContainer>
            <LineChart data={(charts.signups_by_day_30d || []).map((d) => ({ ...d, day: fmtMonthDay(d.date) }))}>
              <CartesianGrid strokeDasharray="3 3" stroke="#C5A04D" strokeOpacity={0.15} />
              <XAxis dataKey="day" tick={{ fill: "#3A2D24", fontSize: 11, fontFamily: "Inter" }} axisLine={{ stroke: "#3A2D24" }} tickLine={false} interval="preserveStartEnd" />
              <YAxis tick={{ fill: "#3A2D24", fontSize: 11, fontFamily: "Inter" }} axisLine={false} tickLine={false} allowDecimals={false} />
              <Tooltip content={<Tip valueFormatter={(v) => `${v} signups`} />} />
              <Line type="monotone" dataKey="count" stroke="#8B1E2C" strokeWidth={2} dot={false} activeDot={{ r: 4, fill: "#8B1E2C" }} />
            </LineChart>
          </ResponsiveContainer>
        </ChartFrame>

        <ChartFrame
          testId="chart-points"
          title="Points economy"
          sublabel="Points awarded daily"
        >
          <ResponsiveContainer>
            <AreaChart data={(charts.points_awarded_by_day_30d || []).map((d) => ({ ...d, day: fmtMonthDay(d.date) }))}>
              <defs>
                <linearGradient id="goldArea" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#C5A04D" stopOpacity={0.55} />
                  <stop offset="100%" stopColor="#C5A04D" stopOpacity={0.05} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#C5A04D" strokeOpacity={0.15} />
              <XAxis dataKey="day" tick={{ fill: "#3A2D24", fontSize: 11, fontFamily: "Inter" }} axisLine={{ stroke: "#3A2D24" }} tickLine={false} interval="preserveStartEnd" />
              <YAxis tick={{ fill: "#3A2D24", fontSize: 11, fontFamily: "Inter" }} axisLine={false} tickLine={false} allowDecimals={false} />
              <Tooltip content={<Tip valueFormatter={(v) => `${v.toLocaleString()} pts`} />} />
              <Area type="monotone" dataKey="points" stroke="#C5A04D" strokeWidth={2} fill="url(#goldArea)" />
            </AreaChart>
          </ResponsiveContainer>
        </ChartFrame>
      </div>

      {/* Tier distribution full-width */}
      <div className="mt-6">
        <ChartFrame
          testId="chart-tiers"
          title="Tier distribution"
          sublabel="Customer breakdown by loyalty tier"
          height={280}
        >
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 items-center h-full">
            <div className="col-span-1 sm:col-span-2 h-full">
              <ResponsiveContainer>
                <PieChart>
                  <Pie
                    data={tierData}
                    dataKey="value"
                    nameKey="name"
                    innerRadius={60}
                    outerRadius={100}
                    paddingAngle={2}
                    strokeWidth={1}
                    stroke="#FAF6EE"
                  >
                    {tierData.map((entry, i) => (
                      <Cell key={i} fill={entry.fill} />
                    ))}
                  </Pie>
                  <Tooltip
                    content={({ active, payload }) =>
                      active && payload && payload.length ? (
                        <div className="bg-napoli-cream-warm border border-napoli-oxblood/30 px-3 py-2 shadow-md">
                          <p className="font-body font-semibold text-[11px] tracking-[0.18em] uppercase text-napoli-oxblood">
                            {payload[0].name}
                          </p>
                          <p className="font-display font-bold text-[18px] text-napoli-espresso mt-1 leading-none">
                            {payload[0].value} customers
                          </p>
                        </div>
                      ) : null
                    }
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <ul className="space-y-3" data-testid="tier-legend">
              {tierData.map((t) => (
                <li key={t.name} className="flex items-center gap-3">
                  <span aria-hidden className="w-3.5 h-3.5" style={{ background: t.fill }} />
                  <span className="font-body font-medium text-[13px] text-napoli-espresso flex-1">{t.name}</span>
                  <span className="font-body font-semibold text-[13px] text-napoli-espresso tabular-nums">
                    {t.value}
                    <span className="ml-1 font-normal text-napoli-espresso-soft text-[11px]">
                      ({totalTierCount > 0 ? Math.round((t.value / totalTierCount) * 100) : 0}%)
                    </span>
                  </span>
                </li>
              ))}
            </ul>
          </div>
        </ChartFrame>
      </div>

      {/* Top customers */}
      <div className="mt-6 bg-napoli-marble border border-napoli-gold/20 p-6" data-testid="top-customers">
        <p className="font-body font-semibold text-[11px] tracking-[0.3em] uppercase text-napoli-oxblood">
          Top customers by lifetime
        </p>
        <p className="mt-1 font-body italic text-[12px] text-napoli-espresso-soft">
          Your loyalty leaderboard.
        </p>
        <ul className="mt-5 divide-y divide-napoli-gold/15">
          {(charts.top_customers_by_lifetime || []).map((c, i) => (
            <li key={c.id} className="flex items-center gap-4 py-3" data-testid={`top-customer-${c.id}`}>
              <span className="w-6 font-body font-bold text-[14px] text-napoli-espresso-soft tabular-nums text-right">
                {i + 1}
              </span>
              <Link
                to={`/admin/customer/${c.id}`}
                className="flex-1 font-body font-semibold text-[14px] text-napoli-espresso hover:text-napoli-oxblood transition-colors"
              >
                {[c.first_name, c.last_name].filter(Boolean).join(" ") || "—"}
              </Link>
              <span className={tierPillClass(c.tier)}>{tierLabel(c.tier)}</span>
              <span className="font-display font-bold text-[18px] text-napoli-gold tabular-nums w-28 text-right">
                {fmtNumber(c.lifetime_points)} pts
              </span>
            </li>
          ))}
          {!(charts.top_customers_by_lifetime || []).length ? (
            <li className="py-6 text-center font-body italic text-[13px] text-napoli-espresso-soft">
              No customers yet.
            </li>
          ) : null}
        </ul>
      </div>

      {/* Recent activity */}
      <div className="mt-6 bg-napoli-marble border border-napoli-gold/20" data-testid="admin-recent-activity">
        <div className="px-6 pt-6">
          <p className="font-body font-semibold text-[11px] tracking-[0.3em] uppercase text-napoli-oxblood">
            Recent activity
          </p>
          <p className="mt-1 font-body italic text-[12px] text-napoli-espresso-soft">
            Last 10 transactions across the team.
          </p>
        </div>
        <ul className="mt-4">
          {(recent_activity || []).length === 0 ? (
            <li className="px-5 py-8 text-center font-body italic text-[13px] text-napoli-espresso-soft">
              No activity yet tonight.
            </li>
          ) : (
            (recent_activity || []).map((tx) => <RecentRow key={tx.id} tx={tx} />)
          )}
        </ul>
        <div className="px-6 py-4 border-t border-napoli-gold/15 text-right">
          <Link
            to="/staff/log"
            data-testid="admin-view-all-activity"
            className="font-body font-semibold text-[11px] tracking-[0.22em] uppercase text-napoli-oxblood hover:text-napoli-oxblood-dark border-b border-napoli-oxblood/60 pb-0.5"
          >
            View all activity →
          </Link>
        </div>
      </div>
    </section>
  );
};

export default AdminDashboardPage;
